<template lang="pug">
//- AuthorAudio / audioDoc: marker names must match `resolveSplitTemplateAudioUrls` S3 keys (see splitTemplateAudioMarkerNames)
template(v-if="audioDoc")
  audio-marker(:name="splitMarkerNames.parent")
  audio-marker(:name="splitMarkerNames.left0" v-if="splitMarkerNames.left0")
  audio-marker(:name="splitMarkerNames.right0" v-if="splitMarkerNames.right0")

//- Parent screen content (heading / text / instructions)
uc-content(:editPath="editPath" :screen="screen")

//- Left panel — normal left[] array
.screen__left(:class="{ 'justify-center': screen['center-y'] }" v-if="screen.left")
  uc-screen(
    :edit-path="`${editPath}.left.${i}`"
    :nested="true"
    :screen="s"
    v-for="(s, i) in screen.left"
  )

//- Left panel — left-edge[] array (base `split` template adds decorative bg images)
.screen__left-edge(:class="{ 'justify-center': screen['center-y'] }" v-if="screen['left-edge']")
  uc-screen(
    :edit-path="`${editPath}.left-edge.${i}`"
    :nested="true"
    :screen="s"
    v-for="(s, i) in screen['left-edge']"
  )
  .screen__left-img.hidden.md_block.absolute.inset-0.m-auto.ml-0.mt-0(
    :class="['z-[-2]', twMerge('w-[40%] bg-left-top bg-contain bg-screen bg-no-repeat', ...classesBg)]"
    :style="{ 'background-image': `url(${screen['left-edge']?.['img-top']})` }"
    v-if="hasEdgeImages && screen['left-edge']?.['img-top']"
  )
  .screen__left-img.hidden.md_block.absolute.inset-0.m-auto.ml-0.mb-0(
    :class="['z-[-2]', twMerge('w-[40%] bg-left-bottom bg-contain bg-screen bg-no-repeat', ...classesBg)]"
    :style="{ 'background-image': `url(${screen['left-edge']?.['img-bottom']})` }"
    v-if="hasEdgeImages && screen['left-edge']?.['img-bottom']"
  )

//- Right panel — normal right[] array
.screen__right(:class="{ 'justify-center': screen['center-y'] }" v-if="screen.right")
  uc-screen(
    :edit-path="`${editPath}.right.${i}`"
    :nested="true"
    :screen="s"
    v-for="(s, i) in screen.right"
  )

//- Right panel — right-edge[] array (base `split` template adds decorative bg images)
.screen__right-edge(:class="{ 'justify-center': screen['center-y'] }" v-if="screen['right-edge']")
  .screen__right-img.hidden.md_block.absolute.inset-0.m-auto.mr-0.mt-0(
    :class="['z-[-2]', twMerge('w-[40%] bg-right-top bg-contain bg-screen bg-no-repeat', ...classesBg)]"
    :style="{ 'background-image': `url(${screen['right-edge']?.['img-top']})` }"
    v-if="hasEdgeImages && screen['right-edge']?.['img-top']"
  )
  .screen__right-img.hidden.md_block.absolute.inset-0.m-auto.mr-0.mb-0(
    :class="['z-[-2]', twMerge('w-[40%] bg-right-bottom bg-contain bg-screen bg-no-repeat', ...classesBg)]"
    :style="{ 'background-image': `url(${screen['right-edge']?.['img-bottom']})` }"
    v-if="hasEdgeImages && screen['right-edge']?.['img-bottom']"
  )
  uc-screen(
    :edit-path="`${editPath}.right-edge.${i}`"
    :nested="true"
    :screen="s"
    v-for="(s, i) in screen['right-edge']"
  )

//- Pass-course completion button (mirrors uc-screen.vue's split blocks)
.col-span-edge.flex.flex-col.items-center.justify-center.mt-8.gap-6.fade-in(
  v-if="screen['pass-course'] && showCloseButton && !wordDoc && !audioDoc"
)
  uc-course-complete(:is-nested="true")
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { stop, playUrlSequence } from '../lib/audio';
import {
  resolveSplitTemplateAudioUrls,
  splitTemplateAudioMarkerNames,
  type NarratableScreen
} from '../lib/useScreenAudio';
import { twMerge } from '../lib/mergeStyles';

const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: UcScreen;
    nested?: boolean;
  }>(),
  { nested: false }
);

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

/**
 * Edge decorative images (img-top / img-bottom inside left-edge / right-edge)
 * are only present on the base `split` template — split-2-1 and split-1-2
 * use the same left/right-edge panel structure but without the decorative imgs.
 */
const hasEdgeImages = computed(() => props.screen.template === 'split');

/** bg-* classes extracted from screen.style, forwarded to edge decorative images. */
const classesBg = computed(() => {
  const bgPrefix = /^([a-z]{2,3}_)?bg-/;
  return (props.screen?.style ?? '')
    .split(' ')
    .filter((c: string) => bgPrefix.test(c));
});

// ---------------------------------------------------------------------------
// Injections shared with uc-screen.vue context
// ---------------------------------------------------------------------------

const showCloseButton = inject('showCloseButton') as Ref<boolean>;
const wordDoc = inject<boolean>('wordDoc');
const audioDoc = inject<boolean>('audioDoc');

/** Keys for `createAudioScript` / AuthorAudio — aligned with Tier 2 `getAudioURL` + runtime queue order. */
const splitMarkerNames = computed(() =>
  splitTemplateAudioMarkerNames(
    props.screen as NarratableScreen,
    props.editPath
  )
);

// ---------------------------------------------------------------------------
// Audio management
// ---------------------------------------------------------------------------
//
// The split template is simultaneously-visible (unlike sticky which is
// scroll-sequenced). On section activation we play the parent screen's intro
// then queue the first left child and first right child in order.
//
// Audio sequence: parent intro → left-0 → right-0
//
// S3 key convention:
//   parent intro → screen.id   (standard intro marker)
//   child intro  → child.id    (standard child marker when id is present)
//               → ${parentId}_${side}_${index}  (synthesised when id is absent)
//
// JiT: resolveChildIntro() builds text from the child's own registered schema,
// using the synthesised id as the stable cache key.
//
// Tier 1 (prop audio): resolveChildIntro() checks child.audio / child.speech
// before any S3/JiT lookup — works correctly whether the id is real or
// synthesised.

const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));

const handleAudio = async () => {
  stop();

  // [parent intro, ...left chain, ...right chain]; panels may expand to several
  // clips when a side wraps its own templates[] chain. Play the first, queue the rest.
  const urls = await resolveSplitTemplateAudioUrls(
    props.screen,
    props.editPath
  );

  // Guard against the section deactivating while JiT was synthesizing.
  if (!sectionActive.value) return;

  playUrlSequence(urls, { owner: props.screen.id });
};

watch(sectionActive, active => {
  if (props.nested) return;
  if (!active) return;
  handleAudio();
});

// The first section on a page never receives a false→true transition because
// uc-scroll-page initialises currentSection after mount. Mirror the same
// onMounted guard used in uc-screen.vue to catch this case.
onMounted(async () => {
  await nextTick();
  if (props.nested) return;
  if (!sectionActive.value) return;
  handleAudio();
});
</script>
